import { Fragment, useState } from "react"

// Icons
import { ChevronDownIcon, FiX } from "@/lib/icons"

// Translations
import { useTrans } from "@/i18n"

// UI
import { cn } from "@/lib/utils"
import {
	Dialog,
	DialogBackdrop,
	Disclosure,
	Popover,
	PopoverGroup,
	PopoverButton,
	PopoverPanel,
	Transition,
	TransitionChild,
} from "@/lib/ui"
import { Button } from "@/components/Button"
import { Radio } from "@/components/form-controls"
import { P } from "@/components/Typography"

// Animations
import {
	motion,
	AnimatePresence,
	getTransitionBackgroundProps,
	getTransitionPopoverProps,
	getTransitionSidebarProps,
} from "@/lib/animations"

// Constants
import type { StateSelectorOptionType } from "./paymentsFilters.constants"

const DISPLAY_ACTIVE_FILTERS = false

interface FiltersProps {
	selectedStateFilter: StateSelectorOptionType
	setSelectedStateFilter: (filter: StateSelectorOptionType) => void
}

export function FiltersDesktop({
	selectedStateFilter,
	setSelectedStateFilter,
}: FiltersProps) {
	const t = useTrans("payments")

	return (
		<>
			{/* Filters */}
			<section aria-labelledby="filters-heading">
				<h2 id="filters-heading" className="sr-only">
					{t("payments:list.filters")}
				</h2>

				<div className="relative z-10">
					<PopoverGroup className="flex items-center divide-x divide-gray-200">
						<Popover className="relative inline-block text-left">
							<PopoverButton
								as="div"
								className="group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900"
							>
								<Button variant="transparent">
									<span className="mr-2">
										{t("payments:list.filter.by_state")}
									</span>
									<AnimatePresence
										initial={false}
										key="filter-state-amount"
									>
										<motion.span
											className="bg-primary-500 overflow-hidden rounded text-xs text-black tabular-nums"
											initial={{
												width: 0,
											}}
											animate={{
												width: "auto",
											}}
											exit={{
												width: 0,
											}}
										>
											<span
												className="px-1.5 py-0.5"
												data-testid="payments:list.filter.by_state.count"
											>
												{t(
													`payments:list.state.${selectedStateFilter?.toLowerCase()}`,
												)}
											</span>
										</motion.span>
									</AnimatePresence>
									<ChevronDownIcon
										className="ml-2 size-5 shrink-0 text-gray-400 group-hover:text-gray-500"
										aria-hidden="true"
									/>
								</Button>
							</PopoverButton>

							<Transition {...getTransitionPopoverProps()}>
								<PopoverPanel className="absolute left-0 mt-2 origin-top-left rounded-md bg-white p-4 shadow-2xl ring-1 ring-black/5 focus:outline-hidden">
									<div
										className="min-w-[300px] space-y-4"
										data-testid="filter-payment-states-desktop"
									>
										<FiltersPaymentStates
											selectedStateFilter={
												selectedStateFilter
											}
											setSelectedStateFilter={
												setSelectedStateFilter
											}
										/>
									</div>
								</PopoverPanel>
							</Transition>
						</Popover>
					</PopoverGroup>
				</div>

				{/* active filters */}
				{!!selectedStateFilter && DISPLAY_ACTIVE_FILTERS ? (
					<div className="mt-4">
						<h3 className="sr-only">
							{t("payments:list.filter.by_state.active.heading")}
						</h3>

						<div className="flex flex-wrap items-center gap-x-3 gap-y-2">
							<Button
								variant="transparent"
								size="small"
								onClick={() =>
									setSelectedStateFilter(selectedStateFilter)
								}
							>
								<span className="sr-only">
									{t(
										"payments:list.filter.by_state.remove_filter",
										{
											state: `payments:list.state.${selectedStateFilter?.toLowerCase()}`,
										},
									)}
								</span>
								<span>
									{t(
										`payments:list.state.${selectedStateFilter?.toLowerCase()}`,
									)}
								</span>
								<FiX className="ml-2" />
							</Button>
						</div>
					</div>
				) : null}
			</section>
		</>
	)
}

/**
 * FiltersMobile
 */
export function FiltersMobile({
	selectedStateFilter,
	setSelectedStateFilter,
}: FiltersProps) {
	const t = useTrans()

	const [open, setOpen] = useState(false)

	return (
		<>
			{/* mobile filters start */}
			<Transition show={open} as={Fragment}>
				<Dialog
					as="div"
					className="fixed inset-0 z-40 flex"
					onClose={setOpen}
				>
					<TransitionChild {...getTransitionBackgroundProps()}>
						<DialogBackdrop className="fixed inset-0 bg-black/25" />
					</TransitionChild>
					<TransitionChild {...getTransitionSidebarProps()}>
						<div className="relative ml-auto flex size-full max-w-xs flex-col overflow-y-auto bg-white py-4 pb-6 shadow-xl">
							<div className="flex items-center justify-between px-4">
								<h2 className="text-lg font-medium text-gray-900">
									{t("payments:list.filters")}
								</h2>
								<button
									type="button"
									className="focus:ring-secondary-500 -mr-2 flex size-10 items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-50 focus:ring-2 focus:outline-hidden"
									onClick={() => setOpen(false)}
								>
									<span className="sr-only">
										{t("common:navigation.menu.close")}
									</span>
									<FiX
										className="size-6"
										aria-hidden="true"
									/>
								</button>
							</div>

							{/* filters */}
							<Disclosure
								as="form"
								className="mt-4 border-t border-gray-200 px-4 py-6"
								defaultOpen={true}
							>
								{({ open }) => (
									<>
										<h3 className="-mx-2 -my-3 flow-root">
											<Disclosure.Button className="flex w-full items-center justify-between bg-white px-2 py-3 text-sm text-gray-400">
												<span className="font-medium text-gray-900">
													{t(
														"payments:list.state.title",
													)}
												</span>
												<span className="ml-6 flex items-center">
													<ChevronDownIcon
														className={cn(
															open
																? "-rotate-180"
																: "rotate-0",
															"h-5 w-5 transform",
														)}
														aria-hidden="true"
													/>
												</span>
											</Disclosure.Button>
										</h3>
										<Disclosure.Panel className="pt-6">
											<div className="space-y-6">
												<FiltersPaymentStates
													selectedStateFilter={
														selectedStateFilter
													}
													setSelectedStateFilter={
														setSelectedStateFilter
													}
												/>
											</div>
										</Disclosure.Panel>
									</>
								)}
							</Disclosure>
						</div>
					</TransitionChild>
				</Dialog>
			</Transition>
			{/* mobile filters end */}

			{/* button to open mobile menu filters */}
			<Button
				type="button"
				variant="transparent"
				className="md:hidden"
				onClick={() => setOpen(true)}
			>
				{t("payments:list.filters")}
			</Button>
		</>
	)
}

/**
 * FiltersPaymentStates
 * @returns
 */
function FiltersPaymentStates({
	selectedStateFilter,
	setSelectedStateFilter,
}: FiltersProps) {
	const t = useTrans("payments")

	return (
		<>
			{/** Open */}
			<label className="group flex cursor-pointer items-center">
				<Radio
					id="filter-payment-states-open"
					data-testid="filter-payment-states-open"
					name="state"
					defaultChecked={selectedStateFilter === "OPEN"}
					onClick={() => {
						setSelectedStateFilter("OPEN")
					}}
				/>
				<P className="ml-3 text-sm text-gray-700 group-hover:text-gray-900">
					{t("payments:list.state.open")}
				</P>
			</label>

			{/** Done */}
			<label className="group flex cursor-pointer items-center">
				<Radio
					id="filter-payment-states-done"
					data-testid="filter-payment-states-done"
					name="state"
					defaultChecked={selectedStateFilter === "DONE"}
					onClick={() => {
						setSelectedStateFilter("DONE")
					}}
				/>
				<P className="ml-3 text-sm text-gray-700 group-hover:text-gray-900">
					{t("payments:list.state.done")}
				</P>
			</label>

			{/** All payments */}
			<label className="group flex cursor-pointer items-center">
				<Radio
					id="filter-payment-states-all"
					data-testid="filter-payment-states-all"
					name="state"
					defaultChecked={selectedStateFilter === "ALL"}
					onClick={() => {
						setSelectedStateFilter("ALL")
					}}
				/>
				<P className="ml-3 text-sm text-gray-700 group-hover:text-gray-900">
					{t("payments:list.state.all")}
				</P>
			</label>
		</>
	)
}
